// import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
// UI
import { createSceneSwitcher } from "$/game/prefabs/ui/SceneSwitcher.ts";
import { RoomIndicatorPanel } from "$/game/prefabs/ui/indicators/RoomIndicatorPanel.ts";
import { CrystalIndicator } from "../prefabs/ui/indicators/CrystalIndicator.ts";
import { NameIndicator } from "$/game/prefabs/ui/indicators/NameIndicator.ts";
// Decor Objects
import { CarpetDecorObject } from "$/game/prefabs/home/CarpetDecorObject.ts";
import { PictureDecorObject } from "$/game/prefabs/home/PictureDecorObject.ts";
import { FlowerDecorObject } from "$/game/prefabs/home/FlowerDecorObject.ts";
import { ClosetDecorObject } from "$/game/prefabs/home/ClosetDecorObject.ts";
// Interactive Objects
import { BowlInteractiveObject } from "$/game/prefabs/home/BowlInteractiveObject.ts";
import { GameboyInteractiveObject } from "$/game/prefabs/home/GameboyInteractiveObject.ts";
import { BasketInteractiveObject } from "$/game/prefabs/home/BasketInteractiveObject.ts";
import { LampInteractiveObject } from "$/game/prefabs/home/LampInteractiveObject.ts";
import { SoapInteractiveObject } from "$/game/prefabs/home/SoapInteractiveObject.ts";
import { PhoneInteractiveObject } from "$/game/prefabs/home/PhoneInteractiveObject.ts";
// Physics Objects
import { BallPhysicsObject } from "$/game/prefabs/home/BallInteractiveObject.ts";
// Pet Game Object
import { PetGameObject } from "$/game/prefabs/PetGameObject.ts";
// Systems
import { CleanPetSystem } from "$/game/systems/CleanPetSystem.ts";
import { PlayBallSystem } from "$/game/systems/PlayBallSystem.ts";
import { PoopSystem } from "$/game/systems/PoopSystem.ts";
import { SleepPetSystem } from "$/game/systems/SleepPetSystem.ts";
import { FoodPetSystem } from "$/game/systems/FoodPetSystem.ts";

export class PetHomeScene extends BasicScene {
  private petGameObject!: PetGameObject;
  private cleanPetSystem!: CleanPetSystem;
  private playBallSystem!: PlayBallSystem;
  private poopSystem!: PoopSystem;
  private sleepPetSystem!: SleepPetSystem;
  private foodPetSystem!: FoodPetSystem;
  private gameCenterX: number = 0;
  private gameCenterY: number = 0;

  constructor() {
    super({
      key: "PetHomeScene",
      physics: {
        default: "matter",
        matter: {
          debug: false,
          gravity: { x: 0, y: 1.8 },
        },
        arcade: {
          debug: false,
        },
      },
    });
  }

  private createUI(): void {
    createSceneSwitcher(this);

    const roomIndicatorPanel = new RoomIndicatorPanel(
      this,
      this.cameras.main.width - 100,
      60,
    );
    this.add.existing(roomIndicatorPanel);

    const crystalIndicator = new CrystalIndicator(this, 70, 60);
    this.add.existing(crystalIndicator);

    const nameIndicator = new NameIndicator(
      this,
      this.cameras.main.width - 65,
      120,
    );
    this.add.existing(nameIndicator);

    this.add.text(60, 120, "⚙", {
      fontSize: "40px",
      color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
    })
      .setOrigin(0.5)
      .setInteractive()
      .on("pointerdown", () => {
        if (this.settings.getSetting("isVibrationEnabled")) {
          this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
        }
        this.overlay.show("MainSettingsOverlay", true);
      });
  }

  create(): void {
    this.gameCenterX = this.cameras.main.width / 2;
    this.gameCenterY = this.cameras.main.height * 0.8;

    this.matter.world.setBounds(
      0,
      0,
      this.scale.width,
      this.scale.height,
    );

    // this.soundManager.playMusic(this.keys.assets.sounds.music.home);

    // Background
    this.add.image(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.atlas,
      this.keys.assets.sprites.backgrounds.backgroundRoom,
    )
      .setDisplaySize(this.cameras.main.width, this.cameras.main.height)
      .setDepth(-1);

    // Decor Items
    const carpetDecorObject = new CarpetDecorObject(
      this,
      this.gameCenterX,
      this.gameCenterY,
    );
    this.add.existing(carpetDecorObject);

    const pictureDecorObject = new PictureDecorObject(
      this,
      this.gameCenterX - 170,
      this.gameCenterY - 600,
    );
    this.add.existing(pictureDecorObject);

    const closetDecorObject = new ClosetDecorObject(
      this,
      this.gameCenterX + 230,
      this.gameCenterY - 180,
    );
    this.add.existing(closetDecorObject);

    const flowerDecorObject = new FlowerDecorObject(
      this,
      closetDecorObject.x - 30,
      closetDecorObject.y - 130,
    );
    this.add.existing(flowerDecorObject);

    // Interactive Items
    const gameboyInteractiveObject = new GameboyInteractiveObject(
      this,
      this.gameCenterX - 220,
      this.gameCenterY - 150,
    );
    this.add.existing(gameboyInteractiveObject);

    const bowlInteractiveObject = new BowlInteractiveObject(
      this,
      this.gameCenterX - 170,
      this.gameCenterY + 120,
    );
    this.add.existing(bowlInteractiveObject);

    const basketInteractiveObject = new BasketInteractiveObject(
      this,
      this.gameCenterX + 300,
      this.gameCenterY - 100,
    );
    this.add.existing(basketInteractiveObject);

    const phoneInteractiveObject = new PhoneInteractiveObject(
      this,
      this.gameCenterX + 170,
      this.gameCenterY + 170,
    );
    this.add.existing(phoneInteractiveObject);

    // Pet Game Object
    this.petGameObject = new PetGameObject(this, carpetDecorObject);
    this.add.existing(this.petGameObject);

    const soapInteractiveObject = new SoapInteractiveObject(
      this,
      this.gameCenterX + 290,
      this.gameCenterY - 290,
    );
    this.add.existing(soapInteractiveObject);

    // Clean Pet System
    this.cleanPetSystem = new CleanPetSystem(
      this,
      this.petGameObject,
      soapInteractiveObject,
    );

    const ballInteractiveObject = new BallPhysicsObject(
      this,
      this.gameCenterX + 170,
      this.gameCenterY + 50,
    );
    this.add.existing(ballInteractiveObject);

    // Play Ball System
    this.playBallSystem = new PlayBallSystem(
      this,
      this.petGameObject,
      ballInteractiveObject,
    );

    // Poop System
    this.poopSystem = new PoopSystem(
      this,
      this.petGameObject,
      basketInteractiveObject,
    );

    const shadowRect = this.add.rectangle(
      this.scale.width / 2,
      this.scale.height / 2,
      this.scale.width,
      this.scale.height,
      0x113333,
      1,
    ).setOrigin(0.5, 0.5)
      .setAlpha(0);

    const lampInteractiveObject = new LampInteractiveObject(
      this,
      shadowRect,
      this.gameCenterX + 190,
      this.gameCenterY - 550,
    );
    this.add.existing(lampInteractiveObject);

    // Sleep Pet System
    this.sleepPetSystem = new SleepPetSystem(
      this,
      this.petGameObject,
      lampInteractiveObject,
    );

    // Food Pet System
    this.foodPetSystem = new FoodPetSystem(
      this,
      this.petGameObject,
      bowlInteractiveObject,
    );

    // this.petSprite = this.physics.add.sprite(
    //   this.gameCenterX,
    //   this.gameCenterY,
    //   this.registry.get("pet")?.type,
    // )
    //   .setOrigin(0.5, 1)
    //   .setInteractive({ useHandCursor: true });

    // this.scheduleNextMovement();

    // UI
    this.createUI();

    this.time.addEvent({
      delay: 5000,
      callback: async () => {
        try {
          await this.api.pet.get();
          const pet = this.registry.get("pet");
          if (pet.is_alive == false) {
            this.transition.toScene(
              "PetDeathScene",
              this.keys.strings.sceneTransitions.petIsDead,
            );
          }
        } catch (error) {
          console.error(
            `Error fetching pet: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          );
        }
      },
      loop: true,
    });
  }

  override update(_time: number, _delta: number): void {
    // Обновляем систему очистки питомца
    this.cleanPetSystem.update();
    // Обновляем систему poop
    this.poopSystem.update();
    // Обновляем систему сна
  }

  destroy(): void {
    this.cleanPetSystem.destroy();
    this.playBallSystem.destroy();
    this.poopSystem.destroy();
    this.sleepPetSystem.destroy();
  }
}
